/**
 * `@`-mention suggestion popover for the comments RichTextEditor.
 *
 * Mirrors the structure of MacroSuggestionPopover — a fixed-positioned
 * list of org members (display name + email), arrow-key navigable,
 * mouse-selectable, dismissed with Escape.
 */

import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import type { SuggestionCallbackProps, SuggestionItem, SuggestionRenderer } from '@chatsdet/ui';

export interface MentionSuggestionItem extends SuggestionItem {
  userId: string;
  displayName: string;
  primaryEmail: string | null;
}

const PopoverContent: React.FC<{
  items: SuggestionItem[];
  command: (item: SuggestionItem) => void;
  highlightedIndex: number;
}> = ({ items, command, highlightedIndex }) => {
  const listRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const highlighted = listRef.current?.children[highlightedIndex] as HTMLElement | undefined;
    highlighted?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  if (items.length === 0) {
    return (
      <div className="z-50 rounded-md border border-border bg-popover shadow-md px-3 py-2 text-sm text-muted-foreground">
        No matches
      </div>
    );
  }

  return (
    <div
      ref={listRef}
      className="z-50 max-h-56 overflow-y-auto rounded-md border border-border bg-popover shadow-md"
      role="listbox"
    >
      {items.map((item, idx) => {
        const mention = item as MentionSuggestionItem;
        const selected = idx === highlightedIndex;
        return (
          <div
            key={mention.id}
            role="option"
            aria-selected={selected}
            className={`cursor-pointer px-3 py-2 text-sm ${
              selected ? 'bg-accent text-accent-foreground' : 'text-foreground hover:bg-accent/50'
            }`}
            onMouseDown={(e) => {
              e.preventDefault();
              command(item);
            }}
          >
            <div className="font-medium">{mention.displayName}</div>
            {mention.primaryEmail && mention.primaryEmail !== mention.displayName && (
              <div className="text-xs text-muted-foreground truncate">{mention.primaryEmail}</div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export function createMentionSuggestionRenderer(): SuggestionRenderer {
  let container: HTMLDivElement | null = null;
  let root: Root | null = null;
  let highlightedIndex = 0;
  let currentProps: SuggestionCallbackProps | null = null;

  function render() {
    if (!container || !root || !currentProps) return;

    const rect = currentProps.clientRect?.();
    if (rect) {
      const popoverWidth = 260;
      const viewportWidth = window.innerWidth;
      const left = Math.min(rect.left, viewportWidth - popoverWidth - 8);
      container.style.position = 'fixed';
      container.style.left = `${Math.max(8, left)}px`;
      container.style.top = `${rect.bottom + 4}px`;
      container.style.width = `${popoverWidth}px`;
    }

    root.render(
      <PopoverContent
        items={currentProps.items}
        command={currentProps.command}
        highlightedIndex={highlightedIndex}
      />
    );
  }

  return {
    onStart(props) {
      container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.zIndex = '50';
      document.body.appendChild(container);
      root = createRoot(container);
      highlightedIndex = 0;
      currentProps = props;
      render();
    },
    onUpdate(props) {
      currentProps = props;
      highlightedIndex = 0;
      render();
    },
    onKeyDown({ event }) {
      if (!currentProps) return false;
      const itemCount = currentProps.items.length;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        highlightedIndex = Math.min(highlightedIndex + 1, itemCount - 1);
        render();
        return true;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        highlightedIndex = Math.max(highlightedIndex - 1, 0);
        render();
        return true;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        if (itemCount > 0 && currentProps.items[highlightedIndex]) {
          event.preventDefault();
          currentProps.command(currentProps.items[highlightedIndex]!);
          return true;
        }
      }
      if (event.key === 'Escape') {
        return true;
      }
      return false;
    },
    onExit() {
      if (root) {
        root.unmount();
        root = null;
      }
      if (container) {
        container.remove();
        container = null;
      }
      currentProps = null;
      highlightedIndex = 0;
    },
  };
}
